import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import { logout } from "../services/authService";

export type UnsavedChangesGuard = {
    isDirty: boolean;
    save: () => Promise<boolean>;
};

type PendingNavigation =
    | { type: "path"; path: string }
    | { type: "logout" }
    | null;

type UnsavedChangesContextValue = {
    registerUnsavedGuard: (guard: UnsavedChangesGuard | null) => void;
    requestNavigate: (to: string) => void;
    requestLogout: () => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
    null
);

function normalizePath(to: string): string {
    if (to.startsWith("http://") || to.startsWith("https://")) {
        try {
            const url = new URL(to);
            return `${url.pathname}${url.search}`;
        } catch {
            return to;
        }
    }
    return to.startsWith("/") ? to : `/${to}`;
}

function isSameLocation(pathWithSearch: string, location: { pathname: string; search: string }) {
    const current = `${location.pathname}${location.search}`;
    return pathWithSearch === current || pathWithSearch === location.pathname;
}

export const UnsavedChangesProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const guardRef = useRef<UnsavedChangesGuard | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [pending, setPending] = useState<PendingNavigation>(null);
    const [isSaving, setIsSaving] = useState(false);

    const registerUnsavedGuard = useCallback(
        (guard: UnsavedChangesGuard | null) => {
            guardRef.current = guard;
            setIsDirty(Boolean(guard?.isDirty));
        },
        []
    );

    const openLeaveModal = useCallback((next: PendingNavigation) => {
        setPending(next);
        setModalOpen(true);
    }, []);

    const proceedPending = useCallback(async () => {
        const next = pending;
        setModalOpen(false);
        setPending(null);
        guardRef.current = null;
        setIsDirty(false);
        if (!next) return;
        if (next.type === "logout") {
            await logout();
            return;
        }
        navigate(next.path);
    }, [pending, navigate]);

    const requestNavigate = useCallback(
        (to: string) => {
            const path = normalizePath(to);
            if (isSameLocation(path, location)) {
                navigate(path);
                return;
            }
            if (guardRef.current?.isDirty) {
                openLeaveModal({ type: "path", path });
                return;
            }
            navigate(path);
        },
        [location, navigate, openLeaveModal]
    );

    const requestLogout = useCallback(() => {
        if (guardRef.current?.isDirty) {
            openLeaveModal({ type: "logout" });
            return;
        }
        void logout();
    }, [openLeaveModal]);

    useEffect(() => {
        if (!isDirty) return;

        const onClick = (event: MouseEvent) => {
            if (event.defaultPrevented) return;
            if (event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const target = event.target as HTMLElement | null;
            const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
            if (!anchor) return;
            if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
                return;
            }

            let url: URL;
            try {
                url = new URL(anchor.href, window.location.origin);
            } catch {
                return;
            }
            if (url.origin !== window.location.origin) return;

            const nextPath = `${url.pathname}${url.search}`;
            if (isSameLocation(nextPath, location)) return;

            event.preventDefault();
            event.stopPropagation();
            openLeaveModal({ type: "path", path: nextPath });
        };

        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [isDirty, location, openLeaveModal]);

    const handleDismiss = useCallback(() => {
        setModalOpen(false);
        setPending(null);
    }, []);

    const handleLeaveWithoutSaving = useCallback(() => {
        void proceedPending();
    }, [proceedPending]);

    const handleSaveAndLeave = useCallback(async () => {
        const guard = guardRef.current;
        if (!guard) {
            await proceedPending();
            return;
        }
        setIsSaving(true);
        try {
            const ok = await guard.save();
            if (ok) {
                await proceedPending();
            }
        } finally {
            setIsSaving(false);
        }
    }, [proceedPending]);

    const value = useMemo(
        () => ({
            registerUnsavedGuard,
            requestNavigate,
            requestLogout,
        }),
        [registerUnsavedGuard, requestNavigate, requestLogout]
    );

    return (
        <UnsavedChangesContext.Provider value={value}>
            {children}
            <ConfirmModal
                open={modalOpen}
                title="Modifications non enregistrées"
                description="Tu as des changements non sauvegardés. Si tu quittes maintenant, ils seront perdus."
                cancelLabel="Quitter sans sauvegarder"
                confirmLabel="Enregistrer"
                confirmVariant="primary"
                isLoading={isSaving}
                handleClose={handleDismiss}
                handleCancel={handleLeaveWithoutSaving}
                handleConfirm={() => {
                    void handleSaveAndLeave();
                }}
            />
        </UnsavedChangesContext.Provider>
    );
};

export function useUnsavedChanges(): UnsavedChangesContextValue {
    const ctx = useContext(UnsavedChangesContext);
    if (!ctx) {
        throw new Error(
            "useUnsavedChanges must be used within UnsavedChangesProvider"
        );
    }
    return ctx;
}

/** Variante sûre hors provider (tests / pages publiques). */
export function useUnsavedChangesOptional(): UnsavedChangesContextValue | null {
    return useContext(UnsavedChangesContext);
}
